import React, {useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useWebsite } from '../../Data/Website';
import Button from '../../Components/Button/Button';
import CustomSelect from '../../Components/Containers/CustomSelect';
import ButtonRow from '../../Components/Button/ButtonRow';
import themeDefaultImage from '../../Images/theme-default.png';
import themeFlatImage from '../../Images/theme-flat.png';
import themeCleanseeImage from '../../Images/theme-cleansee.png';
import { classNames, getContrastColor, useNewWindow } from '../../Services/helpers';
import Icon from '../../Components/Icons/Icon';
import Alert from '../../Components/Alert';

const themes = [
    {
        theme: 'default',
        title: 'Carte',
        description: 'Display your content in elegant cards. Subtle shadows make your content pop.',
        image: themeDefaultImage,
    },
    {
        theme: 'cleansee',
        title: 'Pure',
        description: 'Clean lines and simple icon navigation lets your visitors navigate with a single tap.',
        image: themeCleanseeImage,
    },
    {
        theme: 'flat',
        title: 'Modern',
        description: 'Simple, modern flat design for an elegant website. Ideal if you have great photos.',
        image: themeFlatImage,
    }
];

const colors = [
    '#FF0000',
    '#FF4500',
    '#FF8C00',
    '#FFD700',
    '#FFFF00',
    '#ADFF2F',
    '#7FFF00',
    '#32CD32',
    '#00FF00',
    '#20B2AA',
    '#00FFFF',
    '#00BFFF',
    '#1E90FF',
    '#4169E1',
    '#0000FF',
    '#8A2BE2',
    '#9932CC',
    '#9400D3',
    '#FF00FF',
    '#FF1493',
    '#FF69B4',
    '#FFB6C1',
    '#FFC0CB',
    '#FFDAB9',
    '#FFE4B5',
    '#FFE4C4',
    '#FFDEAD',
    '#F5DEB3',
    '#DEB887',
    '#D2B48C',
    '#BC8F8F',
    '#A0522D',
    '#8B4513',
    '#A52A2A',
    '#800000',
    '#708090',
    '#778899',
    '#2F4F4F',
    '#696969',
    '#808080',
];

const Theme = () => {
    const {website, isSaving, changeTheme} = useWebsite(true);

    return <>
        <TopBar left="/more" title="Website theme" />
        <Page>
            {website && <Inner {...{website, isSaving, changeTheme}} />}
        </Page>
    </>
}

const Inner = ({website, isSaving, changeTheme}) => {
    const [theme, setTheme] = useState(website.theme);
    const [color, setColor] = useState(website.color);

    const handleSave = () => {
        changeTheme(theme, color)
    }

    if (website.type !== 'getsoapy') {
        return <Alert theme="error" title="Not available" message="This feature is only available for getSoapy websites." />
    }

    return <>
        <CustomSelect selected={website.theme}
            onChange={setTheme}
            options={themes.map(t => ({
                value: t.theme,
                body: <ThemePreview {...{theme: t, website}} />
            }))}
        />

        <ColorPicker {...{color, setColor}} />

        <ButtonRow>
            <Button theme="primary" label="Save" onClick={handleSave} loading={isSaving} />
        </ButtonRow>
    </>
}

const ThemePreview = ({website, theme}) => {
    const newWindow = useNewWindow();

    const {title, description, image} = theme;
    return <div className="flex flex-col space-y-2 items-center">
        <h3 className="text-xl text-brand font-bold">{title}</h3>
        <img src={image} alt={theme} className="max-w-[75%] h-auto" />
        <p className="text-sm italic">{description}</p>
        <Button theme="light" icon="LinkExternal" label="Preview" onClick={() => newWindow(`${website.link}?theme=${theme.theme}`)} />
    </div>
}

const ColorPicker = ({color, setColor}) => {
    return <>
        <h3 className="font-bold mb-2">Color</h3>
        <div className="grid grid-cols-4 gap-2">
            {colors.map(c => <div key={c}>
                <div className={classNames(
                        'w-full h-8 place-items-center justify-center flex',
                        color === c ? 'shadow-lg rounded' : 'rounded-lg'
                    )}
                    style={{backgroundColor: c}}
                    onClick={() => setColor(c)}
                >
                    {color === c && <Icon icon="Check" theme="solid" inline className={'fill-'+getContrastColor(c, true)} />}
                </div>
            </div>)}
        </div>
    </>
}

export default Theme;
